import { CompositionDto } from "@repo/api/editor";
import { getAnimatedImageConfig } from "@repo/api/utils/composition";
import { getMediaSrc } from "../utils/media.ts";
import { Img } from "remotion";
import { Video } from "@remotion/media";
import { useMemo } from "react";
import { parseCrop } from "../utils/crop.ts";
import { CreativeProjectTimelineItem } from "@repo/api/creative-project";
import { AnimatedImageDto } from "@repo/api/animated-image";
import { SourceImageDto } from "@repo/api/source-image";
import { msToFrames } from "../utils/timing.ts";

type Props = {
  aspectRatio: CompositionDto["aspectRatio"];
  timelineItem: CreativeProjectTimelineItem;
  sourceImage: SourceImageDto;
  animatedImage?: AnimatedImageDto;
  fps: number;
};

export function getReverseTrimBefore(
  animatedImage: AnimatedImageDto,
  config: CreativeProjectTimelineItem["animatedImageConfigs"][string],
  fps: number,
) {
  if (!config.duration || !animatedImage.durationMs) {
    return undefined;
  }

  const configDurationMs = config.duration * 1000;

  const reverseTrimBeforeMs =
    animatedImage.durationMs - configDurationMs * (config.playbackSpeed ?? 1);

  if (reverseTrimBeforeMs <= 0) {
    return undefined;
  }

  return msToFrames(reverseTrimBeforeMs, fps);
}

export default function AnimatedImage({
  aspectRatio,
  timelineItem,
  sourceImage,
  animatedImage,
  fps,
}: Props) {
  const crop = useMemo(
    () => parseCrop(timelineItem, animatedImage, aspectRatio),
    [timelineItem, animatedImage, aspectRatio],
  );

  if (!animatedImage || animatedImage.status !== "COMPLETED") {
    return (
      <Img
        src={getMediaSrc(sourceImage.src)}
        className="h-full w-full object-cover object-center"
      />
    );
  }

  const config = getAnimatedImageConfig(timelineItem);

  const src = config.reverse
    ? animatedImage.backwardSrc
    : animatedImage.forwardSrc;

  const trimBefore = config.reverse
    ? getReverseTrimBefore(animatedImage, config, fps)
    : undefined;

  return (
    <div className="h-full w-full overflow-visible">
      <div
        style={{
          height: crop?.overflow === "height" ? "max-content" : "100%",
          width: crop?.overflow === "width" ? "max-content" : "100%",
        }}
      >
        <Video
          src={getMediaSrc(src)}
          playbackRate={config.playbackSpeed ?? 1}
          volume={config.volume ?? 0}
          delayRenderTimeoutInMilliseconds={1000 * 90}
          trimBefore={trimBefore}
          objectFit="cover"
          style={{
            height: crop?.overflow === "height" ? "auto" : "100%",
            width: crop?.overflow === "width" ? "auto" : "100%",
            objectPosition: crop ? "top left" : "center",
            transformOrigin: "top left",
            transform: crop
              ? `scale(${crop.scale}) translateX(-${crop.x}%) translateY(-${crop.y}%)`
              : undefined,
          }}
        />
      </div>
    </div>
  );
}
